import { Box, Divider, HStack, Icon, Button } from '@chakra-ui/react'
import { MapPin } from 'react-feather'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'

import createHref from 'lib/createHref'
import { Heading, Caption } from 'atoms/typography'
import { contentMargin } from 'theme/space'
import Markdown from 'components/Markdown'
import WalkMap, { WalkMapContainer, MapStopProps } from 'components/WalkMap'
import Image from 'components/Image'
import Title from 'components/Title'
import Footer from 'components/Footer'
import LayoutContainer from 'components/LayoutContainer'
import Auth from 'components/Auth'

import WalkMetadata from './WalkMetadata'

export interface WalkLandingProps {
  walkName: string
  walkDescription: string
  cityName: string
  organizationName: string
  ageRange: string
  distance: string
  duration: string
  imageUrl: string
  stops: MapStopProps[]
  walkSlug: string
}

const WalkLanding: React.FC<WalkLandingProps> = ({
  walkName,
  walkDescription,
  cityName,
  organizationName,
  ageRange,
  distance,
  duration,
  imageUrl,
  stops,
  walkSlug,
}) => {
  const { t } = useTranslation()

  return (
    <>
      <Title>{walkName}</Title>

      <Auth walkSlug={walkSlug} shouldResumeProgress />

      <WalkMapContainer>
        <WalkMap stops={stops} walkSlug={walkSlug} showProfile />
      </WalkMapContainer>

      <LayoutContainer>
        <Box w={{ base: '100%', md: '83%' }} mx="center">
          <Caption color="charcoal" my={contentMargin.sm}>
            <Icon as={MapPin} mr="2" />
            {cityName}
          </Caption>

          <Heading size="xl" my={contentMargin.sm}>
            {walkName}
          </Heading>

          <Heading size="md" my={contentMargin.sm} as="p">
            {organizationName}
          </Heading>

          <Divider my={contentMargin.lg} aria-hidden />

          <HStack spacing={contentMargin.lg} alignItems="flex-start">
            <Box width={{ base: '100%', sm: '50%', md: '60%' }}>
              <Box mb={contentMargin.lg} as="article">
                <Markdown>{walkDescription}</Markdown>
              </Box>

              <Box
                my={contentMargin.lg}
                display={{ base: 'block', sm: 'none' }}
              >
                <Image url={imageUrl} h="88" />
              </Box>

              <Divider
                my={contentMargin.lg}
                maxW="64"
                mx={{ base: 'center', sm: 'unset' }}
              />

              <Box my={contentMargin.lg}>
                <WalkMetadata
                  ageRange={ageRange}
                  duration={duration}
                  distance={distance}
                />
              </Box>

              {stops.length > 0 ? (
                <Box
                  my={contentMargin.lg}
                  w={{ base: '83%', sm: '100%' }}
                  mx="center"
                >
                  <Link
                    href={createHref({ walk: walkSlug, stop: stops[0].slug })}
                    passHref
                  >
                    <Button as="a">
                      {t('button.continueToStop', { stop: 1 })}
                    </Button>
                  </Link>
                </Box>
              ) : null}
            </Box>
            <Box
              w={{ base: '100%', sm: '50%', md: '40%' }}
              display={{ base: 'none', sm: 'block' }}
            >
              <Image url={imageUrl} h={{ base: '88', sm: '96' }} />
            </Box>
          </HStack>
        </Box>
      </LayoutContainer>

      <Footer />
    </>
  )
}

export default WalkLanding
